import React, { useContext } from 'react';
import { Checkbox } from '@wds/checkbox';
import { Typography } from '@wds/typography';
import { ErrorCard } from '@sm/webassets';
import { Box } from '@wds/box';
import { SearchConsumer } from '../SearchContext';

import CategoriesContext from '../CategoriesContext';
import FilterNavSkeleton from './FilterNavSkeleton';

export const buildCategoryTree = categories => {
  const result = [];

  // Create the "Others" category
  const noParentCategories = {
    id: 0,
    title: 'Others',
    description: '',
    key: 'others',
    parentId: null,
    children: [],
    ordinal: 0,
  };

  // Get the items with no parents
  const parents = categories.filter(item => item.parentId === null);

  // For each parent, add it to the result and find its children
  parents.forEach(parent => {
    const parentWithChildren = { ...parent, children: [] };

    categories.forEach(item => {
      if (item.parentId === parent.id) {
        parentWithChildren.children.push(item);
      }
    });

    // If a parent has no children, add it to the "Others" category
    if (parentWithChildren.children.length === 0) {
      noParentCategories.children.push(parent);
    } else {
      // Otherwise, add it to the result
      result.push(parentWithChildren);
    }
  });

  // Add the "Others" category to the result
  if (noParentCategories.children.length > 0) {
    result.push(noParentCategories);
  }
  // Sort the result array by title
  result.sort((a, b) => a.ordinal - b.ordinal || a.title?.localeCompare(b.title));

  // Sort the children arrays by title
  result.forEach(item => {
    item.children.sort(({ key: aKey = '' }, { key: bKey = '' }) => aKey?.localeCompare(bKey));
  });

  return result;
};

const FilterNav = () => {
  const { isLoading: isCategoriesLoading, categories } = useContext(CategoriesContext);
  const categoryTree = buildCategoryTree(categories);

  return (
    <SearchConsumer>
      {({ filters, updateFilter }) => {
        const isChecked = filter => filters.includes(filter);
        const handleChange = filter => {
          updateFilter(isChecked(filter) ? filters.filter(f => f !== filter) : [...filters, filter]);
        };

        const renderFilter = ({ name, title, key, id }) => (
          <Box key={id || key} mb={4}>
            <Checkbox
              label={name || title}
              id={id || key}
              data-testid="FilterNav__CategoryItem"
              onChange={() => handleChange(id)}
              checked={isChecked(id)}
            />
          </Box>
        );

        const renderCategoryTree = () => {
          return categoryTree.map(parent => (
            <div key={parent.id}>
              <Box my={5}>
                <Typography variant="cardTitle" component="h3" weight="medium">
                  {parent.title}
                </Typography>
              </Box>
              {parent.children.length && parent.children.map(cat => renderFilter(cat))}
            </div>
          ));
        };

        if (isCategoriesLoading) {
          return <FilterNavSkeleton />;
        }

        if (!isCategoriesLoading && categories.length === 0) {
          return <ErrorCard />;
        }

        return (
          <div>
            <Typography variant="sectionTitle" weight="medium">
              Filters
            </Typography>
            <>{renderCategoryTree()}</>
          </div>
        );
      }}
    </SearchConsumer>
  );
};

export default FilterNav;
